
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { TeacherAvailabilityException } from '@/types/teacherAvailability';

export function useTeacherAvailabilityExceptions(teacherCode: string | undefined) {
  const { toast } = useToast();
  const [exceptions, setExceptions] = useState<TeacherAvailabilityException[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Fetch exceptions
  const fetchExceptions = async () => {
    if (!teacherCode) return;

    const { data, error } = await supabase
      .from('teacher_availability_exceptions')
      .select('*')
      .eq('teacher_code', teacherCode)
      .eq('is_active', true)
      .order('exception_date', { ascending: true });

    if (error) {
      console.error('Error fetching exceptions:', error);
      toast({
        title: 'เกิดข้อผิดพลาด',
        description: 'ไม่สามารถโหลดข้อมูลวันพิเศษได้',
        variant: 'destructive'
      });
    } else {
      // Cast the data to proper types
      const typedExceptions: TeacherAvailabilityException[] = (data || []).map(item => ({
        ...item,
        exception_type: item.exception_type as 'NO_WORK' | 'PARTIAL_UNAVAILABLE'
      }));
      setExceptions(typedExceptions);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      await fetchExceptions();
      setIsLoading(false);
    };

    fetchData();
  }, [teacherCode]);

  // Add exception
  const addException = async (exceptionData: {
    exceptionDate: string;
    startTime?: string;
    endTime?: string;
    exceptionType: 'NO_WORK' | 'PARTIAL_UNAVAILABLE';
    reason?: string;
  }) => {
    if (!teacherCode) return false;

    try {
      const { error } = await supabase
        .from('teacher_availability_exceptions')
        .insert({
          teacher_code: teacherCode,
          exception_date: exceptionData.exceptionDate,
          start_time: exceptionData.startTime || null,
          end_time: exceptionData.endTime || null,
          exception_type: exceptionData.exceptionType,
          reason: exceptionData.reason || null
        });

      if (error) throw error;

      await fetchExceptions();
      toast({
        title: 'สำเร็จ',
        description: 'เพิ่มวันพิเศษแล้ว'
      });
      return true;
    } catch (error) {
      console.error('Error adding exception:', error);
      toast({
        title: 'เกิดข้อผิดพลาด',
        description: 'ไม่สามารถเพิ่มวันพิเศษได้',
        variant: 'destructive'
      });
      return false;
    }
  };

  // Update exception
  const updateException = async (id: string, exceptionData: {
    exceptionDate: string;
    startTime?: string;
    endTime?: string;
    exceptionType: 'NO_WORK' | 'PARTIAL_UNAVAILABLE';
    reason?: string;
  }) => {
    try {
      const { error } = await supabase
        .from('teacher_availability_exceptions')
        .update({
          exception_date: exceptionData.exceptionDate,
          start_time: exceptionData.startTime || null,
          end_time: exceptionData.endTime || null,
          exception_type: exceptionData.exceptionType,
          reason: exceptionData.reason || null
        })
        .eq('id', id);

      if (error) throw error;

      await fetchExceptions();
      toast({
        title: 'สำเร็จ',
        description: 'อัพเดทวันพิเศษแล้ว'
      });
      return true;
    } catch (error) {
      console.error('Error updating exception:', error);
      toast({
        title: 'เกิดข้อผิดพลาด',
        description: 'ไม่สามารถอัพเดทวันพิเศษได้',
        variant: 'destructive'
      });
      return false;
    }
  };

  // Delete exception
  const deleteException = async (id: string) => {
    try {
      const { error } = await supabase
        .from('teacher_availability_exceptions')
        .update({ is_active: false })
        .eq('id', id);

      if (error) throw error;

      await fetchExceptions();
      toast({
        title: 'สำเร็จ',
        description: 'ลบวันพิเศษแล้ว'
      });
      return true;
    } catch (error) {
      console.error('Error deleting exception:', error);
      toast({
        title: 'เกิดข้อผิดพลาด',
        description: 'ไม่สามารถลบวันพิเศษได้',
        variant: 'destructive'
      });
      return false;
    }
  };

  return {
    exceptions,
    isLoading,
    addException,
    updateException,
    deleteException,
    refetch: fetchExceptions
  };
}
